import React, { useState, useRef, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ProgereText } from "@/components/ProgereText";
import { SEO } from "@/components/SEO";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Bot, Send, LifeBuoy, Ticket, User, Loader2, CheckCircle2, Sparkles, Mic, Square, Volume2, VolumeX } from "lucide-react";

const QUICK = [
  "À quoi sert le module PUNCH ?",
  "Comment fonctionne DEVIS AI ?",
  "Quels forfaits incluent l'ERP ?",
  "Comment débloquer TAKE OFF ?",
];

const CATEGORIES = [
  { v: "general", l: "Général" },
  { v: "technique", l: "Problème technique" },
  { v: "facturation", l: "Facturation" },
  { v: "module", l: "Question sur un module" },
];
const PRIORITIES = ["basse", "normale", "haute", "urgente"];

function ChatAssistant() {
  const { lang } = useI18n();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis l'assistant PROGÈRE 🤝 Posez-moi une question sur l'écosystème, je réponds tout de suite. Pour un cas précis, ouvrez un billet à droite." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ttsOn, setTtsOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
  };

  const speak = async (text) => {
    try {
      const { data } = await api.post("/support/voice/speak", { text, lang });
      if (!data.audio_base64) return;
      stopAudio();
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      setSpeaking(true);
      await audio.play();
    } catch (err) {
      setSpeaking(false);
    }
  };

  const send = async (text, spokenInput = false) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (ttsOn || spokenInput) speak(data.reply);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Je ne peux pas répondre pour le moment. Ouvrez un billet de support ci-contre et un technicien PROGÈRE vous contactera rapidement.", error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    stopAudio();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size === 0) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "audio.webm");
          fd.append("lang", lang);
          const { data } = await api.post("/support/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data.text || "").trim();
          if (text) send(text, true);
          else toast.error(lang === "en" ? "No speech detected." : "Aucune parole détectée.");
        } catch (err) {
          toast.error(lang === "en" ? "Transcription failed." : "Transcription impossible.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error(lang === "en" ? "Microphone access denied." : "Accès au micro refusé.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const toggleTts = () => {
    setTtsOn((v) => {
      if (v) stopAudio();
      return !v;
    });
  };

  const busy = loading || transcribing;

  return (
    <div className="flex flex-col rounded-xl border border-white/10 bg-[#0a0a0a] overflow-hidden h-[560px]" data-testid="support-chat">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#EAB308]/10 to-transparent">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#EAB308]/15 border border-[#EAB308]/30">
          <Bot className="w-5 h-5 text-[#EAB308]" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-white flex items-center gap-2">
            Assistant IA PROGÈRE
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-green-400">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> {lang === "en" ? "online" : "en ligne"}
            </span>
          </div>
          <div className="text-xs text-white/45">{lang === "en" ? "Instant answers before the technician" : "Réponses instantanées avant le technicien"}</div>
        </div>
        <button
          onClick={toggleTts}
          data-testid="voice-tts-toggle"
          aria-label="Toggle voice"
          title={ttsOn ? (lang === "en" ? "Voice on" : "Voix activée") : (lang === "en" ? "Voice off" : "Voix coupée")}
          className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
            ttsOn ? "border-[#EAB308]/40 bg-[#EAB308]/15 text-[#EAB308]" : "border-white/10 bg-white/5 text-white/40"
          } ${speaking ? "animate-pulse" : ""}`}
        >
          {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${m.role === "user" ? "bg-[#dc143c]/15 border border-[#dc143c]/30" : "bg-[#EAB308]/15 border border-[#EAB308]/30"}`}>
              {m.role === "user" ? <User className="w-4 h-4 text-[#dc143c]" /> : <Bot className="w-4 h-4 text-[#EAB308]" />}
            </div>
            <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#dc143c]/15 text-white" : m.error ? "bg-[#dc143c]/10 text-red-200 border border-[#dc143c]/20" : "bg-white/5 text-white/85"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#EAB308]/15 border border-[#EAB308]/30">
              <Bot className="w-4 h-4 text-[#EAB308]" />
            </div>
            <div className="rounded-lg px-4 py-3 bg-white/5 flex items-center gap-2 text-xs text-white/50">
              <Loader2 className="w-4 h-4 animate-spin" />
              {transcribing ? (lang === "en" ? "Transcribing…" : "Transcription…") : ""}
            </div>
          </div>
        )}
      </div>

      <div className="px-5 pt-3 pb-4 border-t border-white/10">
        <div className="flex flex-wrap gap-2 mb-3">
          {QUICK.map((q) => (
            <button
              key={q}
              onClick={() => send(q)}
              disabled={busy}
              data-testid="chat-quick"
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60 hover:text-white hover:border-[#EAB308]/40 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={transcribing || loading}
            data-testid="voice-record-btn"
            aria-label="Microphone"
            title={recording ? (lang === "en" ? "Stop & send" : "Arrêter & envoyer") : (lang === "en" ? "Speak" : "Parler")}
            className={`h-11 w-11 shrink-0 flex items-center justify-center rounded-md border transition-colors ${
              recording ? "border-[#dc143c] bg-[#dc143c]/20 text-[#dc143c] animate-pulse" : "border-white/15 bg-black/40 text-white/70 hover:border-[#EAB308]/50 hover:text-[#EAB308]"
            }`}
          >
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
          </button>
          <Input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={recording ? (lang === "en" ? "Listening…" : "À l'écoute…") : (lang === "en" ? "Type your question..." : "Écrivez votre question...")}
            className="bg-black/40 border-white/15 text-white h-11 placeholder:text-white/30"
          />
          <Button
            data-testid="chat-send"
            onClick={() => send()}
            disabled={busy}
            className="h-11 px-4 bg-[#EAB308] text-black rounded-md hover:opacity-90 transition-opacity"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TicketForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "general", priority: "normale", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/support/tickets", form);
      setDone(data.ticket.ref);
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "", category: "general", priority: "normale", message: "" });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Impossible d'envoyer le billet. Vérifiez votre courriel et réessayez.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-6 lg:p-7" data-testid="ticket-form">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#dc143c]/15 border border-[#dc143c]/30">
          <Ticket className="w-5 h-5 text-[#dc143c]" />
        </div>
        <div>
          <div className="font-semibold text-white">Ouvrir un billet de support</div>
          <div className="text-xs text-white/45">Un technicien PROGÈRE vous répondra</div>
        </div>
      </div>

      {done && (
        <div className="mb-5 flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-4 py-2.5 text-sm text-green-300" data-testid="ticket-success">
          <CheckCircle2 className="w-4 h-4" /> Billet <b className="font-mono">{done}</b> créé avec succès.
        </div>
      )}

      {error && (
        <div className="mb-5 rounded-md border border-[#dc143c]/40 bg-[#dc143c]/10 px-4 py-2.5 text-sm text-red-300" data-testid="ticket-error">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="ticket-name" required placeholder="Nom complet" value={form.name} onChange={set("name")} className="bg-black/40 border-white/15 text-white h-11 placeholder:text-white/30" />
          <Input data-testid="ticket-email" required type="email" placeholder="Courriel" value={form.email} onChange={set("email")} className="bg-black/40 border-white/15 text-white h-11 placeholder:text-white/30" />
        </div>
        <Input data-testid="ticket-subject" required placeholder="Sujet" value={form.subject} onChange={set("subject")} className="bg-black/40 border-white/15 text-white h-11 placeholder:text-white/30" />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-white/50">Catégorie</Label>
            <select data-testid="ticket-category" value={form.category} onChange={set("category")} className="w-full h-11 rounded-md bg-black/40 border border-white/15 text-white text-sm px-3">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v} className="bg-[#0a0a0a]">{c.l}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-white/50">Priorité</Label>
            <select data-testid="ticket-priority" value={form.priority} onChange={set("priority")} className="w-full h-11 rounded-md bg-black/40 border border-white/15 text-white text-sm px-3 capitalize">
              {PRIORITIES.map((p) => <option key={p} value={p} className="bg-[#0a0a0a] capitalize">{p}</option>)}
            </select>
          </div>
        </div>
        <Textarea data-testid="ticket-message" required placeholder="Décrivez votre demande..." value={form.message} onChange={set("message")} className="bg-black/40 border-white/15 text-white min-h-[120px] placeholder:text-white/30" />
        <Button data-testid="ticket-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#EAB308] text-black font-semibold rounded-md gap-2 hover:opacity-90 transition-opacity">
          <Send className="w-4 h-4" /> {loading ? "Envoi..." : "Soumettre le billet"}
        </Button>
      </form>
    </div>
  );
}

export default function SupportPage() {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="min-h-screen progere-bg" data-testid="support-page">
      <SEO title="Support" path="/support" description="Centre d'aide PROGÈRE : assistant IA 24/7, billets de support et documentation pour l'écosystème de gestion de la construction." />
      <Header />
      <section className="relative pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-0 pg-grid-bg opacity-40" />
        <div className="absolute top-0 right-1/4 w-[400px] h-[400px] pg-radial-gold" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#EAB308]/30 bg-[#EAB308]/5 px-4 py-1.5 text-xs font-mono uppercase tracking-[0.2em] text-[#EAB308]">
            <LifeBuoy className="w-3.5 h-3.5" /> PROGÈRE SUPPORT
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
            Centre d'aide <ProgereText />
          </h1>
          <p className="mt-3 max-w-2xl text-white/60 text-lg">
            Posez votre question à notre assistant IA pour une réponse immédiate, ou
            ouvrez un billet pour parler à un technicien. Tout est centralisé et automatisé.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid gap-6 lg:grid-cols-2 items-start">
          <ChatAssistant />
          <TicketForm />
        </div>
      </section>
      <Footer />
    </div>
  );
}
